import React, { useCallback, useMemo, useState } from 'react';
import { ColorMatrixFilter, Graphics, Sprite } from 'pixi.js';

import useLoader, { getTexture } from '@/game/useLoader';
import { GemType, GemState } from '@/framework/gem';

export type Props = {
  type?: GemType;
  state?: GemState;
  width: number;
  height: number;
  /** Opponent gem when both players share a colour (preview: all opponent gems). */
  rival?: boolean;
};

const Gem = ({ width, height, type, state = 'normal', rival = false }: Props) => {
  const { loaded } = useLoader();
  const [maskRef, setMaskRef] = useState<Sprite | null>(null);

  const name = useMemo(() => {
    if (!type) return null;
    return `gem-${type}`;
  }, [type]);

  const rivalFilter = useMemo(() => {
    const filter = new ColorMatrixFilter();
    filter.saturate(-0.28);
    filter.brightness(0.94, true);
    filter.hue(-16, true);
    return filter;
  }, []);

  const drawRivalMark = useCallback(
    (g: Graphics) => {
      const x = width * 0.58;
      const y = height * 0.5;
      const len = width * 0.22;

      g.clear();
      g.setStrokeStyle({
        width: Math.max(2, width * 0.055),
        color: 0xd8e4f0,
        alpha: 0.72,
        cap: 'round',
      });
      g.moveTo(x, y);
      g.lineTo(x + len * 0.35, y + len);
      g.stroke();
    },
    [width, height],
  );

  if (!loaded) return null;
  if (!type || !name) return null;

  const texture = getTexture(name);
  const borderTexture = getTexture('border-default');
  const mask = getTexture('broken-mask');
  const shadow = getTexture('broken-shadow');
  const anchor = 0;
  const broken = state === 'broken';

  return (
    <pixiContainer>
      {state === 'selected' && (
        <pixiSprite texture={borderTexture} anchor={anchor} width={width} height={height} />
      )}
      {broken && (
        <>
          <pixiSprite
            ref={setMaskRef}
            texture={mask}
            anchor={anchor}
            width={width}
            height={height}
          />
          <pixiSprite texture={shadow} anchor={anchor} width={width} height={height} />
        </>
      )}
      <pixiContainer mask={broken ? maskRef : null}>
        <pixiSprite
          texture={texture}
          filters={rival ? [rivalFilter] : []}
          anchor={anchor}
          width={width}
          height={height}
        />
        {rival && <pixiGraphics draw={drawRivalMark} />}
      </pixiContainer>
    </pixiContainer>
  );
};

export default Gem;
